// src/views/Inventory/FormPindahPos.js
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  Modal,
  Table,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../../configs"; // SESUAIKAN PATH

const { Text } = Typography;

export default function FormPemusnahan() {
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // ====== STATE MODAL PILIH BARANG ======
  const [modalVisible, setModalVisible] = useState(false);
  const [itemStockLoading, setItemStockLoading] = useState(false);
  const [itemStockData, setItemStockData] = useState([]);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // EDIT MODE
  const { id } = useParams();
  const [deletedIds, setDeletedIds] = useState([]);

  // Fetch Data on Edit
  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const fetchData = async (pemusnahanId) => {
    setLoading(true); // Need to define setLoading or reuse itemStockLoading? 
    // Better create pageLoading state? Or just use setItemStockLoading temporarily or add new state.
    // I will just use setItemStockLoading for now or add new state.
    // I'll add isEditLoading state.
    try {
      const res = await axiosInstance.get(`/pemusnahan/${pemusnahanId}`);
      const data = res.data.data;

      // Map header
      const header = {
        no_pemusnahan: data.no_pemusnahan,
        tanggal_pemusnahan: data.tanggal_pemusnahan ? dayjs(data.tanggal_pemusnahan) : null,
        catatan: data.catatan,
        no_persetujuan_kppbc: data.no_persetujuan_kppbc,
        tanggal_persetujuan_kppbc: data.tanggal_persetujuan_kppbc ? dayjs(data.tanggal_persetujuan_kppbc) : null,
        no_ba_pemusnahan: data.no_ba_pemusnahan,
        tanggal_ba_pemusnahan: data.tanggal_ba_pemusnahan ? dayjs(data.tanggal_ba_pemusnahan) : null,
      };

      // Map details
      const details = (data.details || []).map(d => ({
        id_pemusnahan_detail: d.id_pemusnahan_detail,
        seri: d.seri,
        nopen: d.nopen,
        item_code: d.item_code,
        lot_asal: d.lot_asal,
        jumlah_kemasan_asal: d.jumlah_kemasan_asal, // might differ from current stock if stock changed? 
        // Wait, DB stores snapshot of 'asal' at time of creation? Yes.
        jumlah_kemasan_tujuan: d.jumlah_kemasan_tujuan
      }));

      form.setFieldsValue({
        ...header,
        details
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil data pemusnahan");
    } finally {
      setLoading(false);
    }
  };

  // Load item stock ketika modal dibuka
  const fetchItemStock = async () => {
    setItemStockLoading(true);
    try {
      const res = await axiosInstance.get("/item-stock", {
        params: {
          page: 1,
          limit: 100, // sesuaikan kalau mau paging beneran
        },
      });
      const list = res.data?.data || [];
      setItemStockData(list);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat data stok barang");
    } finally {
      setItemStockLoading(false);
    }
  };

  // Auto-generate No Pemusnahan
  useEffect(() => {
    const currentNo = form.getFieldValue("no_pemusnahan");
    if (!currentNo) {
      // PEM-YYYYMMDD-ALB
      const formattedDate = dayjs().format("YYYYMMDD");
      const autoNo = `PEM-${formattedDate}-ALB`;
      form.setFieldsValue({
        no_pemusnahan: autoNo
      });
    }
  }, [form]);

  const openModalPilihBarang = () => {
    setModalVisible(true);
    fetchItemStock();
  };

  const closeModalPilihBarang = () => {
    setModalVisible(false);
  };

  // ====== HANDLE PILIH SATU BARANG UNTUK DITAMBAHKAN KE DETAIL ======
  const handleSelectItemFromModal = (record) => {
    const currentDetails = form.getFieldValue("details") || [];

    // mapping field dari /api/item-stock ke detail payload:
    // seri  -> record.seri
    // nopen -> record.nopen
    // item_code -> record.item_code
    // lot_asal -> record.lokasi_lot
    // jumlah_kemasan_asal -> record.jumlah_kemasan
    const newRow = {
      seri: record.seri,
      nopen: record.nopen,
      item_code: record.item_code,
      lot_asal: record.lokasi_lot,
      jumlah_kemasan_asal: record.jumlah_kemasan,
      lot_tujuan: "",
      jumlah_kemasan_tujuan: undefined,
    };

    form.setFieldsValue({
      details: [...currentDetails, newRow],
    });

    message.success("Barang berhasil ditambahkan ke daftar pemusnahan");
    closeModalPilihBarang();
  };

  // ====== SUBMIT FORM KE API /pemusnahan ======
  const handleFinish = async (values) => {
    const details = values.details || [];

    if (!details.length) {
      message.error("Minimal harus ada satu baris detail pemusnahan");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const details = values.details || [];
    // User requested Indonesian format / MySQL compatible YYYY-MM-DD HH:mm:ss
    const nowIso = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const headerPayload = {
      id_pemusnahan: 0,
      no_pemusnahan: values.no_pemusnahan,
      tanggal_pemusnahan: values.tanggal_pemusnahan
        ? dayjs(values.tanggal_pemusnahan).format("YYYY-MM-DD")
        : null,
      catatan: values.catatan || 0,
      created_by: "1",
      created_at: nowIso,
      // New Fields
      no_persetujuan_kppbc: values.no_persetujuan_kppbc,
      tanggal_persetujuan_kppbc: values.tanggal_persetujuan_kppbc
        ? dayjs(values.tanggal_persetujuan_kppbc).format("YYYY-MM-DD")
        : null,
      no_ba_pemusnahan: values.no_ba_pemusnahan,
      tanggal_ba_pemusnahan: values.tanggal_ba_pemusnahan
        ? dayjs(values.tanggal_ba_pemusnahan).format("YYYY-MM-DD")
        : null,
    };

    const detailsPayload = details.map((row, idx) => ({
      id_pemusnahan_detail: 0,
      id_pemusnahan: 0,
      seri: row.seri || idx + 1,
      nopen: row.nopen,
      item_code: row.item_code,
      lot_asal: row.lot_asal,
      jumlah_kemasan_asal: Number(row.jumlah_kemasan_asal) || 0,
      lot_tujuan: row.lot_tujuan,
      jumlah_kemasan_tujuan: Number(row.jumlah_kemasan_tujuan) || 0,
      created_by: "1",
      created_at: nowIso,
    }));

    const payload = {
      header: headerPayload,
      details: detailsPayload,
      deleted_detail_ids: deletedIds
    };

    try {
      setSaving(true);
      if (id) {
        await axiosInstance.put(`/pemusnahan/${id}/with-details`, payload);
        message.success("Data pemusnahan berhasil diperbarui");
      } else {
        await axiosInstance.post("/pemusnahan", payload);
        message.success("Data pemusnahan berhasil disimpan");
      }

      if (!id) {
        form.resetFields();
        // Regenerate number
        const dateStr = dayjs().format("YYYYMMDD");
        form.setFieldsValue({
          no_pemusnahan: `PEM-${dateStr}-ALB`
        });
      } else {
        // Reload data
        fetchData(id);
        setDeletedIds([]);
      }
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data pemusnahan");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  // ====== KOLOM TABLE MODAL ITEM-STOCK ======
  const itemStockColumns = [
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: "Item Code",
      dataIndex: "item_code",
      key: "item_code",
    },
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 80,
    },
    {
      title: "Nopen",
      dataIndex: "nopen",
      key: "nopen",
      width: 120,
    },
    {
      title: "Jumlah Kemasan",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 140,
    },
    {
      title: "Lot / Lokasi",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 120,
    },
    {
      title: "Aksi",
      key: "action",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => handleSelectItemFromModal(record)}>
          Pilih
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card
        className="kt-portlet"
        bodyStyle={{ padding: 24 }}
        style={{
          borderRadius: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          border: "1px solid #F3F6F9",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            tanggal_pemusnahan: dayjs(),
            details: [],
          }}
        >
          {/* ===== HEADER TITLE ===== */}
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <Typography.Title level={3} style={{ marginBottom: 4 }}>
                Form Pemusnahan Barang
              </Typography.Title>
              <Text type="secondary">
                Catat pemusnahan barang.
              </Text>
            </Col>
          </Row>

          {/* ===== HEADER FORM ===== */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>NO. PEMUSNAHAN *</Text>}
                name="no_pemusnahan"
                rules={[
                  { required: true, message: "No Pemusnahan wajib diisi" },
                ]}
              >
                <Input placeholder="PEM-YYY-MM-DD/ALB" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>TGL. PEMUSNAHAN *</Text>}
                name="tanggal_pemusnahan"
                rules={[
                  { required: true, message: "Tanggal pemusnahan wajib diisi" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ borderBottom: '1px solid #f0f0f0', margin: '24px 0' }}></div>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>NO. PERSETUJUAN KPPBC *</Text>}
                name="no_persetujuan_kppbc"
                rules={[{ required: true, message: "Wajib diisi" }]}
              >
                <Input placeholder="No. Persetujuan KPPBC" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>TGL. PERSETUJUAN KPPBC *</Text>}
                name="tanggal_persetujuan_kppbc"
                rules={[{ required: true, message: "Wajib diisi" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>NO. BA PEMUSNAHAN *</Text>}
                name="no_ba_pemusnahan"
                rules={[{ required: true, message: "Wajib diisi" }]}
              >
                <Input placeholder="No. BA Pemusnahan" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>TGL. BA PEMUSNAHAN *</Text>}
                name="tanggal_ba_pemusnahan"
                rules={[{ required: true, message: "Wajib diisi" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={24}>
              <Form.Item label={<Text strong>Catatan</Text>} name="catatan">
                <Input.TextArea placeholder="Catatan (opsional)" />
              </Form.Item>
            </Col>
          </Row>

          {/* ===== TABEL DETAIL PINDAH POS ===== */}
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 8,
              borderRadius: 4,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            {/* Header kolom ala Metronic */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "0.6fr 1.4fr 2.2fr 1.4fr 1.8fr 1.8fr 0.8fr",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
                alignItems: "center",
              }}
            >
              <div>SERI</div>
              <div>NOPEN</div>
              <div>ITEM CODE</div>
              <div>LOT ASAL</div>
              <div>JML KEMASAN ASAL</div>
              <div>JML DIMUSNAHKAN</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="details">
              {(fields, { remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "0.6fr 1.4fr 2.2fr 1.4fr 1.8fr 1.8fr 0.8fr",
                        padding: "8px 16px",
                        borderTop: "1px solid #E4E6EF",
                        alignItems: "center",
                      }}
                    >
                      {/* SERI (dibaca saja dari field.seri) */}
                      <Form.Item
                        {...field}
                        name={[field.name, "seri"]}
                        fieldKey={[field.fieldKey, "seri"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input disabled placeholder="Seri" />
                      </Form.Item>

                      {/* NOPEN */}
                      <Form.Item
                        {...field}
                        name={[field.name, "nopen"]}
                        fieldKey={[field.fieldKey, "nopen"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input disabled placeholder="Nopen" />
                      </Form.Item>

                      {/* ITEM CODE */}
                      <Form.Item
                        {...field}
                        name={[field.name, "item_code"]}
                        fieldKey={[field.fieldKey, "item_code"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input disabled placeholder="Item Code" />
                      </Form.Item>

                      {/* LOT ASAL */}
                      <Form.Item
                        {...field}
                        name={[field.name, "lot_asal"]}
                        fieldKey={[field.fieldKey, "lot_asal"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input disabled placeholder="Lot Asal" />
                      </Form.Item>

                      {/* JML KEMASAN ASAL */}
                      <Form.Item
                        {...field}
                        name={[field.name, "jumlah_kemasan_asal"]}
                        fieldKey={[field.fieldKey, "jumlah_kemasan_asal"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input
                          disabled
                          type="number"
                          placeholder="Jumlah Kemasan Asal"
                        />
                      </Form.Item>

                      {/* JML KEMASAN TUJUAN (editable) -> JML DIMUSNAHKAN */}
                      <Form.Item
                        {...field}
                        name={[field.name, "jumlah_kemasan_tujuan"]}
                        fieldKey={[field.fieldKey, "jumlah_kemasan_tujuan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                        rules={[
                          {
                            required: true,
                            message: "Jumlah dimusnahkan wajib diisi",
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder="Jml Dimusnahkan"
                        />
                      </Form.Item>

                      {/* AKSI - HAPUS ROW */}
                      <div style={{ textAlign: "center" }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined style={{ color: "#F64E60" }} />}
                          onClick={() => {
                            const row = form.getFieldValue(['details', field.name]);
                            if (row && row.id_pemusnahan_detail) {
                              setDeletedIds(prev => [...prev, row.id_pemusnahan_detail]);
                            }
                            remove(field.name);
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Jika belum ada detail */}
                  {fields.length === 0 && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderTop: "1px solid #E4E6EF",
                        color: "#B5B5C3",
                        fontStyle: "italic",
                      }}
                    >
                      Belum ada barang yang dimusnahkan. Klik{" "}
                      <b>Pilih Barang</b> untuk menambah.
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </Card>

          {/* ===== ACTIONS BAWAH TABEL ===== */}
          <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
            <Col>
              <Button icon={<SearchOutlined />} onClick={openModalPilihBarang}>
                Pilih Barang dari Stok
              </Button>
            </Col>
            <Col>
              <Space>
                <Button onClick={handleCancel}>Batal</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    minWidth: 140,
                    fontWeight: 600,
                  }}
                >
                  Simpan
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* ===== MODAL PILIH BARANG DARI ITEM-STOCK ===== */}
      <Modal
        title="Pilih Barang Dari Stok"
        open={modalVisible}
        onCancel={closeModalPilihBarang}
        footer={null}
        width={900}
      >
        <Table
          rowKey="id_item_stock"
          loading={itemStockLoading}
          columns={itemStockColumns}
          dataSource={itemStockData}
          pagination={false}
          size="small"
        />
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Pemusnahan Barang"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data pemusnahan ini?</p>
            <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4, marginBottom: 16 }}>
              <p style={{ margin: 0 }}>
                <strong>No Pemusnahan:</strong> {pendingValues.no_pemusnahan}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong>{" "}
                {pendingValues.tanggal_pemusnahan
                  ? dayjs(pendingValues.tanggal_pemusnahan).format("DD MMMM YYYY")
                  : "-"}
              </p>
            </div>

            <Text strong>Detail Barang:</Text>
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Item Code</th>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Lot Asal</th>
                    <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Jml Dimusnahkan</th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.details || []).map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{item.item_code}</td>
                      <td style={{ padding: "4px 8px" }}>{item.lot_asal}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>{item.jumlah_kemasan_tujuan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
